if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.autoSlideMs = 4000;
        this.setupInlineVideoPlayback();
        this.setupMobileGallerySwipe();
        this.startGalleryAutoSlide();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (this.mql.matches) this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches) {
            const items = Array.from(activeMedia.parentElement.querySelectorAll('.product__media-item'));
            this.goToMobileSlide(items.indexOf(activeMedia));
            this.startGalleryAutoSlide();
            return;
          }
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        const inlineVideo = activeItem.querySelector('video.product__media-inline-video');
        if (inlineVideo) {
          const playPromise = inlineVideo.play();
          if (playPromise !== undefined) playPromise.catch(() => {});
        }
      }

      setupInlineVideoPlayback() {
        const inlineVideos = this.querySelectorAll('video.product__media-inline-video');
        if (!inlineVideos.length || !('IntersectionObserver' in window)) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                const playPromise = video.play();
                if (playPromise !== undefined) playPromise.catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: [0, 0.6, 1] }
        );

        inlineVideos.forEach((video) => observer.observe(video));
      }

      gallerySlides() {
        const slider = this.querySelector('.product__media-list');
        if (!slider) return [];
        return Array.from(slider.querySelectorAll('.product__media-item'));
      }

      goToMobileSlide(index, instant) {
        const slider = this.querySelector('.product__media-list');
        const items = this.gallerySlides();
        if (!slider || !items.length) return;

        const next = ((index % items.length) + items.length) % items.length;
        const wrapping =
          this.mobileSlideIndex != null &&
          ((this.mobileSlideIndex === items.length - 1 && next === 0) ||
            (this.mobileSlideIndex === 0 && next === items.length - 1));

        if (instant || wrapping) slider.classList.add('is-instant');
        this.mobileSlideIndex = next;
        slider.style.setProperty('--product-gallery-index', String(next));
        if (instant || wrapping) {
          requestAnimationFrame(() => slider.classList.remove('is-instant'));
        }

        items.forEach((item, i) => {
          item.classList.toggle('is-active', i === next);
        });

        const counter = this.elements.viewer && this.elements.viewer.querySelector('.slider-counter--current');
        if (counter) counter.textContent = String(next + 1);

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${items[next].dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }

        this.playActiveMedia(items[next]);
      }

      advanceGallerySlide(direction) {
        const items = this.gallerySlides();
        if (items.length < 2) return;
        const current = this.mobileSlideIndex || 0;
        this.goToMobileSlide(current + direction);
      }

      startGalleryAutoSlide() {
        this.stopGalleryAutoSlide();
        if (!this.mobileQuery.matches) return;
        if (this.gallerySlides().length < 2) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.autoSlideTimer = window.setInterval(() => {
          if (document.hidden || !this.mobileQuery.matches) return;
          this.advanceGallerySlide(1);
        }, this.autoSlideMs);
      }

      stopGalleryAutoSlide() {
        if (this.autoSlideTimer) {
          window.clearInterval(this.autoSlideTimer);
          this.autoSlideTimer = null;
        }
      }

      setupMobileGallerySwipe() {
        const slider = this.querySelector('.product__media-list');
        if (!slider) return;

        this.mobileSlideIndex = 0;
        let startX = 0;
        let startY = 0;
        let tracking = false;

        slider.addEventListener(
          'touchstart',
          (event) => {
            if (!this.mobileQuery.matches || !event.touches[0]) return;
            tracking = true;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            this.stopGalleryAutoSlide();
          },
          { passive: true }
        );

        slider.addEventListener(
          'touchmove',
          (event) => {
            if (!tracking || !this.mobileQuery.matches || !event.touches[0]) return;
            const dx = event.touches[0].clientX - startX;
            const dy = event.touches[0].clientY - startY;
            if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
              event.preventDefault();
            }
          },
          { passive: false }
        );

        slider.addEventListener(
          'touchend',
          (event) => {
            if (!tracking || !this.mobileQuery.matches || !event.changedTouches[0]) {
              tracking = false;
              return;
            }
            tracking = false;
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
              this.advanceGallerySlide(dx < 0 ? 1 : -1);
            }
            this.startGalleryAutoSlide();
          },
          { passive: true }
        );

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.stopGalleryAutoSlide();
          else this.startGalleryAutoSlide();
        });

        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', () => this.startGalleryAutoSlide());
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
